import React, { useState, useEffect, useCallback } from 'react';
import { getRequest, postRequest, patchRequest, deleteRequest } from 'apis/ApiInstence';
import { toast } from 'react-toastify';
import { FaEdit, FaTrash, FaPlus, FaArrowLeft, FaLayerGroup } from 'react-icons/fa';
import './ProjectContentUpdate.css';

const ProjectContentUpdate = () => {
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  
  const [formData, setFormData] = useState({
    project_type: 'Residential',
    slug: '',
    main_heading: '',
    intro_heading: '',
    intro_description: '',
    bullet_points: [''],
    status: 'active',
  });
  const [selectedFile, setSelectedFile] = useState(null);

  const fetchContents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRequest('project-page-content');
      if (res.data.success) {
        setContents(res.data.data);
      }
    } catch (error) {
      toast.error('Failed to load contents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContents();
  }, [fetchContents]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleBulletChange = (index, value) => {
    const newBullets = [...(formData.bullet_points || [])];
    newBullets[index] = value;
    setFormData(prev => ({ ...prev, bullet_points: newBullets }));
  };

  const addBullet = () => {
    setFormData(prev => ({ ...prev, bullet_points: [...(prev.bullet_points || []), ''] }));
  };

  const removeBullet = (index) => {
    const newBullets = (formData.bullet_points || []).filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, bullet_points: newBullets }));
  };

  const resetForm = () => {
    setFormData({
      project_type: 'Residential',
      slug: '',
      main_heading: '',
      intro_heading: '',
      intro_description: '',
      bullet_points: [''],
      status: 'active',
    });
    setSelectedFile(null);
    setIsEditing(false);
    setCurrentId(null);
  };

  const handleAddNew = () => {
    resetForm();
    setIsEditing(false);
    setView('form');
  };

  const handleEdit = (content) => {
    let bullets = [''];
    if (content.bullet_points) {
      if (Array.isArray(content.bullet_points)) {
        bullets = content.bullet_points;
      } else if (typeof content.bullet_points === 'string') {
        try {
          bullets = JSON.parse(content.bullet_points);
        } catch (e) {
          bullets = [content.bullet_points];
        }
      }
    }

    setFormData({
      project_type: content.project_type,
      slug: content.slug,
      main_heading: content.main_heading,
      intro_heading: content.intro_heading,
      intro_description: content.intro_description,
      bullet_points: bullets,
      status: content.status,
    });
    setIsEditing(true);
    setCurrentId(content.id);
    setView('form');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();
    Object.keys(formData).forEach(key => {
      if (key === 'bullet_points') {
        data.append(key, JSON.stringify((formData[key] || []).filter(b => b && b.trim() !== '')));
      } else {
        data.append(key, formData[key]);
      }
    });
    if (selectedFile) {
      data.append('image', selectedFile);
    }

    try {
      if (isEditing) {
        await patchRequest(`project-page-content/${currentId}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Content updated successfully');
      } else {
        await postRequest('project-page-content', data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Content added successfully');
      }
      setView('list');
      resetForm();
      fetchContents();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this content?')) {
      try {
        await deleteRequest(`project-page-content/${id}`);
        toast.success('Content deleted');
        fetchContents();
      } catch (error) {
        toast.error('Failed to delete');
      }
    }
  };

  if (view === 'form') {
    return (
      <div className="project-content-page">
        <div className="page-container">
          <div className="d-flex align-items-center mb-4">
            <button className="btn btn-light mr-4" onClick={() => setView('list')}>
              <FaArrowLeft className="mr-2" /> Back to List
            </button>
            <h2 className="mb-0 font-weight-bold text-dark">{isEditing ? 'Edit Page Content' : 'Add New Page Content'}</h2>
          </div>

          <div className="content-card">
            <form onSubmit={handleSubmit} className="card-body">
              <div className="row">
                <div className="col-md-6 form-group">
                  <label htmlFor="project_type">Category Type</label>
                  <select id="project_type" name="project_type" className="form-control" value={formData.project_type} onChange={handleChange}>
                    <option value="Residential">Residential</option>
                    <option value="Commercial">Commercial</option>
                    <option value="Industrial">Industrial</option>
                    <option value="Townships">Townships</option>
                    <option value="Farmhouses">Farmhouses</option>
                  </select>
                </div>
                <div className="col-md-6 form-group">
                  <label htmlFor="slug">SEO Slug (URL)</label>
                  <input id="slug" type="text" name="slug" className="form-control" value={formData.slug} onChange={handleChange} required placeholder="e.g. residential-construction" />
                </div>
                <div className="col-md-12 form-group">
                  <label htmlFor="main_heading">Main Page Heading</label>
                  <input id="main_heading" type="text" name="main_heading" className="form-control" value={formData.main_heading} onChange={handleChange} required />
                </div>
                <div className="col-md-12 form-group">
                  <label htmlFor="image">Banner / Featured Image</label>
                  <input id="image" type="file" className="form-control" onChange={handleFileChange} accept="image/*" />
                </div>
                <div className="col-md-12 form-group">
                  <label htmlFor="intro_heading">Intro Section Heading</label>
                  <input id="intro_heading" type="text" name="intro_heading" className="form-control" value={formData.intro_heading} onChange={handleChange} required />
                </div>
                <div className="col-md-12 form-group">
                  <label htmlFor="intro_description">Intro Section Description</label>
                  <textarea id="intro_description" name="intro_description" className="form-control" rows="6" value={formData.intro_description} onChange={handleChange} required />
                </div>
                
                <div className="col-md-12 form-group">
                  <label htmlFor="bullet_0">Key Features / Bullet Points</label>
                  {(formData.bullet_points || []).map((point, index) => (
                    <div key={index} className="input-group">
                      <input id={`bullet_${index}`} type="text" className="form-control" value={point} onChange={(e) => handleBulletChange(index, e.target.value)} placeholder={`Feature #${index + 1}`} />
                      <button type="button" className="btn btn-danger" onClick={() => removeBullet(index)}>&times;</button>
                    </div>
                  ))}
                  <button type="button" className="btn btn-secondary mt-2" onClick={addBullet}>
                    <FaPlus className="mr-2" size={12} /> Add More Feature
                  </button>
                </div>

                <div className="col-md-12 form-group mt-3">
                  <label htmlFor="status">Page Status</label>
                  <select id="status" name="status" className="form-control" value={formData.status} onChange={handleChange}>
                    <option value="active">Active (Visible on Web)</option>
                    <option value="inactive">Inactive (Hidden)</option>
                  </select>
                </div>
              </div>
              
              <div className="d-flex justify-content-end mt-5 gap-3">
                <button type="button" className="btn btn-light mr-3" onClick={() => setView('list')}>Cancel</button>
                <button type="submit" className="btn btn-primary px-5">{isEditing ? 'Save Changes' : 'Save Content'}</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="project-content-page">
      <div className="page-container">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="font-weight-bold mb-1 text-dark">Project Page Contents</h2>
            <p className="text-muted small mb-0 font-weight-normal">Design and manage specific page content for project categories.</p>
          </div>
          <button className="btn btn-primary" onClick={handleAddNew}>
            <FaPlus className="mr-2" /> Add New Content
          </button>
        </div>

        <div className="content-card">
          <div className="table-responsive">
            <table className="table custom-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Category</th>
                  <th>SEO Slug</th>
                  <th>Heading</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" className="text-center py-5 text-muted">Loading...</td></tr>
                ) : contents.map((item) => (
                  <tr key={item.id}>
                    <td className="font-weight-bold text-primary">#{item.id}</td>
                    <td>
                      <div className="d-flex align-items-center">
                        <FaLayerGroup className="mr-2 text-warning" />
                        <span className="badge badge-info">{item.project_type}</span>
                      </div>
                    </td>
                    <td><code className="small text-muted">{item.slug}</code></td>
                    <td className="font-weight-bold">{item.main_heading}</td>
                    <td>
                      <span className={`badge ${item.status === 'active' ? 'badge-success' : 'badge-danger'}`}>
                        {item.status}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex">
                        <button className="btn btn-sm btn-outline-primary mr-2" title="Edit" onClick={() => handleEdit(item)}>
                          <FaEdit />
                        </button>
                        <button className="btn btn-sm btn-outline-danger" title="Delete" onClick={() => handleDelete(item.id)}>
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && contents.length === 0 && (
                  <tr><td colSpan="6" className="text-center py-5 text-muted">No content records found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectContentUpdate;
